<template>
    <div class="ai-chat-widget">
        <!-- Floating Button -->
        <el-button class="floating-btn" type="primary" :icon="ChatRound" circle @click="toggleChat" />

        <!-- Chat Interface -->
        <transition name="slide-up">
            <div v-if="isOpen" class="chat-container">
                <div class="chat-header">
                    <div class="header-left">
                        <img :src="logo" class="logo" alt="Logo">
                        <span>智能助手</span>
                    </div>
                    <div class="header-right">
                        <el-button type="text" :icon="Minus" @click="minimize" />
                        <el-button type="text" :icon="Close" @click="close" />
                    </div>
                </div>

                <div class="chat-messages">
                    <div v-for="(message, index) in messages" :key="index" class="message"
                        :class="{ 'ai-message': message.sender === 'ai', 'user-message': message.sender === 'user' }">
                        <div class="message-content">
                            <div v-if="message.type === 'text'">{{ message.content }}</div>
                            <img v-else-if="message.type === 'image'" :src="message.content" class="message-image">
                        </div>
                        <div class="message-time">{{ formatTime(message.time) }}</div>
                    </div>
                    <div v-if="isLoading" class="loading-indicator">
                        <span class="dot">.</span>
                        <span class="dot">.</span>
                        <span class="dot">.</span>
                    </div>
                </div>

                <div class="quick-questions" v-if="showQuickQuestions">
                    <h4>您可能想问...</h4>
                    <div class="question-grid">
                        <el-button v-for="(question, index) in quickQuestions" :key="index"
                            @click="sendQuestion(question)">
                            {{ question }}
                        </el-button>
                    </div>
                </div>

                <div class="chat-input">
                    <div class="input-actions">
                        <el-button type="text" :icon="Paperclip" @click="handleAttachment" />
                        <el-button type="text" :icon="Microphone" @click="startVoiceInput" />
                    </div>
                    <el-input v-model="userInput" type="textarea" :rows="3" placeholder="请输入您的问题..."
                        @keydown.enter="handleKeyDown">
                        <template #append>
                            <el-button type="primary" :icon="Promotion" @click="sendMessage" />
                        </template>
                    </el-input>
                    <div v-if="currentAttachment" class="attachment-preview">
                        <img v-if="currentAttachment.type.startsWith('image')" :src="currentAttachment.url"
                            class="preview-image">
                        <div v-else class="file-info">
                            <span>{{ currentAttachment.name }}</span>
                            <el-button type="text" icon="Close" @click="currentAttachment = null" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import {
    ChatRound,
    Minus,
    Close,
    Promotion,
    Paperclip,
    Microphone
} from '@element-plus/icons-vue'
import config from '@/config';

const isOpen = ref(false)
const showQuickQuestions = ref(true)
const userInput = ref('')
const isLoading = ref(false)
const currentAttachment = ref(null)
const messages = ref([
    {
        sender: 'ai',
        type: 'text',
        content: '您好！我是智能客服助手，请问有什么可以帮您？',
        time: new Date()
    }
])

const logo = '/imgs/wyl-logo.png'

const quickQuestions = ref([
    '产品如何使用？',
    '价格是多少？',
    '如何联系客服？',
    '常见问题解答'
])

// Context-based recommendations
const updateRecommendations = () => {
    const path = window.location.pathname
    if (path.includes('/product')) {
        quickQuestions.value = [
            '这个产品的规格参数是什么？',
            '有几种颜色可选？',
            '保修政策是怎样的？',
            '同类产品比较'
        ]
    } else if (path.includes('/payment')) {
        quickQuestions.value = [
            '支持哪些支付方式？',
            '发票如何开具？',
            '退款流程是什么？',
            '分期付款方案'
        ]
    } else if (path.includes('/account')) {
        quickQuestions.value = [
            '如何修改密码？',
            '账户安全设置',
            '绑定手机/邮箱',
            '注销账户流程'
        ]
    }
}

const toggleChat = () => {
    isOpen.value = !isOpen.value
    if (isOpen.value) {
        updateRecommendations()
    }
}

const minimize = () => {
    // TODO: Implement minimize functionality
}

const close = () => {
    isOpen.value = false
}

const sendQuestion = (question) => {
    userInput.value = question
    sendMessage()
}

let eventSource = null

const sendMessage = () => {
    if (!userInput.value.trim() && !currentAttachment.value) return

    // Add user message
    const userMessage = {
        sender: 'user',
        type: currentAttachment.value ? 'image' : 'text',
        content: currentAttachment.value || userInput.value,
        time: new Date()
    }
    messages.value.push(userMessage)

    userInput.value = ''
    currentAttachment.value = null
    isLoading.value = true

    // Close previous SSE connection if exists
    if (eventSource) {
        eventSource.close()
    }

    // Create new SSE connection
    const normalUrl = config.apiBaseUrl + `/ai/springai/testchat?userMsg=${encodeURIComponent(userMessage.content)}`
    eventSource = new EventSource(normalUrl)

    let aiResponse = {
        sender: 'ai',
        type: 'text',
        content: '',
        time: new Date()
    }

    eventSource.onmessage = (event) => {
        const data = event.data

        aiResponse.content += data
        // Update last message or add new if needed
        const lastMessage = messages.value[messages.value.length - 1]
        if (lastMessage.sender === 'ai') {
            lastMessage.content = aiResponse.content
        } else {
            messages.value.push({ ...aiResponse })
        }
    }

    eventSource.onerror = () => {
        isLoading.value = false
        eventSource.close()
    }
    eventSource.addEventListener('done', () => {
        eventSource.close();
    });
}

const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const handleAttachment = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
        const file = e.target.files[0]
        if (file) {
            currentAttachment.value = {
                type: file.type,
                name: file.name,
                url: URL.createObjectURL(file)
            }
        }
    }
    input.click()
}

const startVoiceInput = () => {
    // TODO: Implement voice input
    console.log('Voice input not yet implemented')
}

const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        sendMessage()
    }
}
</script>

<style scoped>
.ai-chat-widget {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 9999;
}

@media (max-width: 768px) {
    .ai-chat-widget {
        right: 0;
        bottom: 0;
        left: 0;
    }
}

.floating-btn {
    width: 56px;
    height: 56px;
    font-size: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    animation: breathe 2s infinite ease-in-out;
}

.floating-btn:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    animation: none;
}

@keyframes breathe {

    0%,
    100% {
        opacity: 0.9;
        transform: scale(1);
    }

    50% {
        opacity: 1;
        transform: scale(1.02);
    }
}

.chat-container {
    width: 400px;
    height: 600px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
}

@media (max-width: 768px) {
    .chat-container {
        width: 100%;
        height: 100vh;
        border-radius: 0;
    }
}

.chat-header {
    padding: 0 16px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--el-color-primary);
    color: white;
    border-radius: 8px 8px 0 0;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.logo {
    width: 24px;
    height: 24px;
}

.header-right {
    display: flex;
    gap: 4px;
}

.chat-messages {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.message {
    max-width: 80%;
    padding: 8px 12px;
    border-radius: 12px;
    position: relative;
}

@media (max-width: 768px) {
    .message {
        max-width: 90%;
    }

    .ai-message {
        margin-right: auto;
    }

    .user-message {
        margin-left: auto;
    }
}

.ai-message {
    align-self: flex-start;
    background: #f5f5f5;
    color: #333;
}

.user-message {
    align-self: flex-end;
    background: var(--el-color-primary);
    color: white;
}

.message-time {
    font-size: 10px;
    color: var(--el-text-color-secondary);
    margin-top: 4px;
    text-align: right;
}

.ai-message .message-time {
    color: var(--el-text-color-secondary);
}

.user-message .message-time {
    color: rgba(255, 255, 255, 0.7);
}

.message-image {
    max-width: 100%;
    border-radius: 8px;
}

.loading-indicator {
    align-self: flex-start;
    padding: 8px 12px;
}

@media (max-width: 768px) {
    .loading-indicator {
        padding: 12px 16px;
    }

    .dot {
        font-size: 18px;
    }
}

.dot {
    animation: dotPulse 1.5s infinite ease-in-out;
    opacity: 0;
}

.dot:nth-child(1) {
    animation-delay: 0s;
}

.dot:nth-child(2) {
    animation-delay: 0.2s;
}

.dot:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes dotPulse {

    0%,
    100% {
        opacity: 0;
    }

    50% {
        opacity: 1;
    }
}

.quick-questions {
    padding: 0 16px 16px;
}

.quick-questions h4 {
    margin: 0 0 8px;
    color: var(--el-text-color-secondary);
    font-weight: normal;
}

@media (max-width: 768px) {
    .quick-questions {
        padding: 0 12px 12px;
    }

    .quick-questions h4 {
        font-size: 16px;
        margin-bottom: 12px;
    }
}

.question-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

@media (max-width: 768px) {
    .question-grid {
        grid-template-columns: 1fr;
    }

    .question-grid .el-button {
        width: 100%;
    }
}

.chat-input {
    padding: 16px;
    border-top: 1px solid var(--el-border-color);
}

@media (max-width: 768px) {
    .chat-input {
        padding: 8px;
    }

    .chat-input :deep(.el-textarea__inner) {
        min-height: 60px !important;
        font-size: 16px;
    }

    .chat-input :deep(.el-button) {
        padding: 12px;
    }
}

.input-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
}

.attachment-preview {
    margin-top: 8px;
    padding: 8px;
    border-radius: 4px;
    background-color: #f5f7fa;
    border: 1px dashed #dcdfe6;
}

.preview-image {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
}

.file-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    background-color: #f5f7fa;
    border-radius: 4px;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}
</style>
